import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import BASE_URL from '../config';
import { Button } from "react-bootstrap";
import axios from "axios";

const DatabaseTransaction = () => {
  const [table, setTable] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [zipFilter, setZipFilter] = useState("");
  const [iframeSrc, setIframeSrc] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [showWebsiteDropdown, setShowWebsiteDropdown] = useState(false);
  const [websiteRequired, setWebsiteRequired] = useState(false);
  const [showDownloadRecent, setShowDownloadRecent] = useState(false);
  const [heading, setHeading] = useState("Upload CSV File");
  const [heading2, setHeading2] = useState("Export Data");
  const [statusMessage, setStatusMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [rowProgress, setRowProgress] = useState("Starting Upload...");
  const [showProgress, setShowProgress] = useState(false);
  const [iframeKey, setIframeKey] = useState(0); // For iframe refresh

  // Auto-refresh iframe every 500 seconds when visible
  useEffect(() => {
    let refreshInterval;
    if (showIframe) {
      refreshInterval = setInterval(() => {
        setIframeKey(prevKey => prevKey + 1); // This will force iframe to reload
      }, 10000); // 500 seconds = 500,000 milliseconds
    }
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [showIframe]);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}api/proxy/download-recent-suppressions`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Recent_Suppressions.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    setStartDate(oneWeekAgo.toISOString().split("T")[0]);
    setEndDate(today.toISOString().split("T")[0]);
  }, []);

  const handleTableChange = (value) => {
    setTable(value);
    setCsvFile(null);
    switch (value) {
      case "dbo.tblRefinance":
        setIframeSrc("/show-refinance");
        setShowIframe(true);
        setShowDownloadRecent(false);
        setShowWebsiteDropdown(false);
        break;
      case "dbo.LegacyData":
        setIframeSrc("/show-legacy");
        setShowIframe(true);
        setShowWebsiteDropdown(false);
        setShowDownloadRecent(false);
        break;
      case "dbo.rgrsuppressions":
        setIframeSrc("/show-rgrsuppressions");
        setShowIframe(true);
        setShowWebsiteDropdown(true);
        setShowDownloadRecent(true);
        break;
      case "dbo.keeper":
        setIframeSrc("/show-keeper");
        setShowIframe(true);
        setShowDownloadRecent(false);
        setShowWebsiteDropdown(false);
        break;
      default:
        setShowIframe(false);
        setShowDownloadRecent(false);
        setShowWebsiteDropdown(false);
        break;
    }
    setHeading(value ? `Upload CSV File to ${value}` : "Upload CSV File");
    setHeading2(value ? `Export CSV File From ${value}` : "Export Data");
  };

  const getUploadURL = () => {
    switch (table) {
      case "dbo.LegacyData":
        return `${BASE_URL}api/proxy/upload_legacy`;
      case "dbo.tblRefinance":
        return `${BASE_URL}api/proxy/upload_refinance`;
      case "dbo.rgrsuppressions":
        return `${BASE_URL}api/proxy/upload_rgrsuppression`;
      case "dbo.keeper":
        return `${BASE_URL}api/proxy/upload_keeper`;
      default:
        return null;
    }
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = reader.result;
          const lines = text
            .split("\n")
            .map(line => line.trim())
            .filter(line => line);
          if (lines.length < 2) {
            reject(new Error("CSV must contain headers and at least one row."));
            return;
          }
          const headers = lines[0].split(",");
          const rows = lines.slice(1).map(line => line.split(","));
          resolve({ headers, rows });
        } catch (e) {
          reject(new Error("Failed to parse CSV"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };

  const processCSV = async () => {
    if (!csvFile || !table) {
      setStatusMessage("Please select a table and a CSV file.");
      return;
    }
    if (table === "dbo.rgrsuppressions" && !selectedWebsite) {
      setStatusMessage("Please select a website for RGR Suppressions.");
      return;
    }

    setIsUploading(true);
    setShowProgress(true);
    setStatusMessage("Processing CSV...");

    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim() !== '');
      const headers = lines[0].split(',');
      const rows = lines.slice(1);
      const totalRows = rows.length;
      const chunkSize = 500;
      let processedRows = 0;

      const uploadUrl = getUploadURL();
      if (!uploadUrl) {
        throw new Error("Table not recognized.");
      }

      for (let i = 0; i < rows.length; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        processedRows += chunk.length;
        
        const progressPercent = (processedRows / totalRows) * 100;
        setUploadProgress(progressPercent);
        setRowProgress(`Uploaded ${processedRows} / ${totalRows} rows`);

        const payload = {
          headers,
          chunk,
          dataType: table
        };

        if (table === "dbo.rgrsuppressions") {
          payload.website = selectedWebsite;
        }

        const response = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      setStatusMessage(`Upload successful! Processed ${totalRows} rows.`);
      setUploadProgress(100);
    } catch (err) {
      setStatusMessage(`Upload failed: ${err.message}`);
      console.error("Error:", err);
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setShowProgress(false);
        setUploadProgress(0);
      }, 2000);
    }
  };

  return (
    <div className="container mt-5">
      <h4 className="text-center">{heading}</h4>
      <div className="mb-3">
        <select
          className="form-select"
          value={table}
          onChange={(e) => handleTableChange(e.target.value)}
        >
          <option value="">Select A Table</option>
          <option value="dbo.LegacyData">LegacyData</option>
          <option value="dbo.tblRefinance">Refinance</option>
          <option value="dbo.rgrsuppressions">RGRSuppressions</option>
          <option value="dbo.keeper">Keeper</option>
        </select>
      </div>
      {showWebsiteDropdown && (
        <div className="mb-3">
          <label htmlFor="website" className="form-label">Select Website:</label>
          <select
            id="website"
            className="form-select"
            value={selectedWebsite}
            onChange={(e) => setSelectedWebsite(e.target.value)}
            required
          >
            <option value="">Select Website</option>
            <option value="Optizmo">Optizmo</option>
            <option value="RgrRyan">RgrRyan</option>
            <option value="Mark Email sorting">Mark Email sorting</option>
            <option value="Charith Email sorting">Charith Email sorting</option>
          </select>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">Choose CSV File</label>
        <input
          type="file"
          className="form-control"
          accept=".csv"
          onChange={(e) => setCsvFile(e.target.files[0])}
          disabled={!table}
        />
      </div>

      <button
        className="btn btn-success"
        onClick={processCSV}
        disabled={isUploading || !table}
      >
        {isUploading ? "Uploading..." : "Upload CSV"}
      </button>
      {showProgress && (
        <div id="progress-container" className="mt-4">
          <div id="progress-bar" className="progress" style={{ height: '25px' }}>
            <div
              id="progress"
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div id="row-progress" className="mt-2 text-center">{rowProgress}</div>
        </div>
      )}
      {statusMessage && (
        <div className="mt-3">
          <p>{statusMessage}</p>
        </div>
      )}

      {showIframe && (
        <div className="mt-4">
          <iframe
            key={iframeKey} // This will force refresh when key changes
            src={iframeSrc}
            style={{ width: "100%", height: "400px" }}
            title="Data View"
          ></iframe>
        </div>
      )}

      <div className="container mt-5">
        <h4 className="text-center">{heading2}</h4>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Start Date:</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">End Date:</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 mb-3">
            <input
              className="form-control"
              placeholder="Email"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
            />
          </div>
          <div className="col-md-3 mb-3">
            <input
              className="form-control"
              placeholder="City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            />
          </div>
          <div className="col-md-3 mb-3">
            <input
              className="form-control"
              placeholder="State"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            />
          </div>
          <div className="col-md-3 mb-3">
            <input
              className="form-control"
              placeholder="Zip"
              value={zipFilter}
              onChange={(e) => setZipFilter(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={handleDownload}>
            Download CSV
          </button>
          <Button 
            id="download-recent-btn"
            variant="success"
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? 'Downloading...' : 'Download Recent CSV'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseTransaction;